/**
 * 清溪天成模版 - 导航栏
 * 
 * 设计：毛玻璃悬浮导航 + 底部波浪装饰
 * 与所有其他模板不同的排版：导航栏悬浮于页面上方，使用毛玻璃效果，
 * 下方有波浪形装饰分割线
 */

import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSiteConfig } from "@/context/SiteConfigContext";
import { useAuth } from "@/hooks/useAuth";
import { getNavCategories } from "@/utils/api";

export default function CrystalNavbar() {
  const navigate = useNavigate();
  const { siteConfig } = useSiteConfig();
  const { isLoggedIn, user, logout } = useAuth();

  const siteName = siteConfig?.site_name || siteConfig?.name || "";
  const logo = siteConfig?.logo || "";
  const enableVip = siteConfig?.enable_vip !== false;

  const [scrolled, setScrolled] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [navCategories, setNavCategories] = useState<Array<{ id: string; name: string; slug: string; icon: string }>>([]);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", handleScroll);
    getNavCategories().then(setNavCategories).catch(() => {});
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = () => {
    logout();
    setUserMenuOpen(false);
    navigate("/");
  };

  const displayCategories = navCategories.length > 0 ? navCategories.slice(0, 5) : [];

  return (
    <>
      <nav
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
          scrolled
            ? "bg-white/80 backdrop-blur-xl shadow-lg shadow-cyan-500/5 border-b border-cyan-100/50"
            : "bg-white/60 backdrop-blur-md"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2.5 shrink-0">
              {logo ? (
                <img src={logo} alt={siteName} className="h-9" />
              ) : (
                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center text-white font-bold text-sm shadow-md shadow-cyan-400/30">
                  {siteName ? siteName.charAt(0).toUpperCase() : "C"}
                </div>
              )}
              <span className="font-bold text-gray-800 text-lg hidden sm:block">{siteName || "清溪天成"}</span>
            </Link>

            {/* 桌面导航 */}
            <div className="hidden lg:flex items-center gap-1">
              <Link to="/" className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-cyan-600 hover:bg-cyan-50 rounded-xl transition-all">
                首页
              </Link>
              <Link to="/category" className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-cyan-600 hover:bg-cyan-50 rounded-xl transition-all">
                分类浏览
              </Link>
              {displayCategories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-cyan-600 hover:bg-cyan-50 rounded-xl transition-all"
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            {/* 右侧操作区 */}
            <div className="flex items-center gap-3">
              <Link
                to="/publish"
                className="hidden sm:flex items-center gap-1.5 px-5 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 text-white text-sm font-semibold rounded-full hover:from-cyan-600 hover:to-blue-700 transition-all shadow-md shadow-cyan-500/25"
              >
                <i className="ri-add-line" />发布
              </Link>

              {isLoggedIn ? (
                <div className="relative" ref={menuRef}>
                  <button
                    onClick={() => setUserMenuOpen(!userMenuOpen)}
                    className="flex items-center gap-2 p-1.5 pl-4 bg-cyan-50 hover:bg-cyan-100 rounded-full transition-all border border-cyan-100"
                  >
                    <span className="text-sm font-medium text-gray-700 max-w-[100px] truncate">
                      {user?.nickname || user?.username || "用户"}
                    </span>
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center text-white text-xs font-bold shadow-sm">
                      {(user?.nickname || user?.username || "U").charAt(0).toUpperCase()}
                    </div>
                  </button>

                  {userMenuOpen && (
                    <div className="absolute right-0 top-full mt-2 w-52 bg-white/95 backdrop-blur-xl rounded-2xl shadow-xl shadow-cyan-500/10 border border-cyan-100/50 overflow-hidden py-1 z-50">
                      <div className="px-4 py-3 border-b border-gray-50">
                        <div className="text-sm font-semibold text-gray-800">{user?.nickname || user?.username}</div>
                        <div className="text-xs text-gray-400 mt-0.5">欢迎回来</div>
                      </div>
                      <Link to="/dashboard" className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-600 hover:bg-cyan-50 transition-colors" onClick={() => setUserMenuOpen(false)}>
                        <i className="ri-user-line text-cyan-500" />会员中心
                      </Link>
                      <Link to="/dashboard/my-listings" className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-600 hover:bg-cyan-50 transition-colors" onClick={() => setUserMenuOpen(false)}>
                        <i className="ri-file-list-line text-cyan-500" />我的发布
                      </Link>
                      {enableVip && (
                        <Link to="/vip" className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-600 hover:bg-cyan-50 transition-colors" onClick={() => setUserMenuOpen(false)}>
                          <i className="ri-vip-crown-line text-cyan-500" />VIP特权
                        </Link>
                      )}
                      <div className="border-t border-gray-50 mt-1 pt-1">
                        <button onClick={handleLogout} className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-500 hover:bg-red-50 hover:text-red-600 transition-colors">
                          <i className="ri-logout-box-line" />退出登录
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  to="/login"
                  className="flex items-center gap-1.5 px-5 py-2 border-2 border-cyan-200 text-cyan-600 text-sm font-semibold rounded-full hover:bg-cyan-50 transition-all"
                >
                  <i className="ri-user-line" />登录
                </Link>
              )}

              {/* 移动端菜单按钮 */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="lg:hidden w-10 h-10 flex items-center justify-center rounded-xl hover:bg-gray-100 transition-colors"
              >
                <i className={`text-xl text-gray-600 ${mobileMenuOpen ? "ri-close-line" : "ri-menu-line"}`} />
              </button>
            </div>
          </div>
        </div>

        {/* 移动端菜单 */}
        {mobileMenuOpen && (
          <div className="lg:hidden bg-white/95 backdrop-blur-xl border-t border-cyan-50 px-4 py-4 space-y-1">
            <Link to="/" onClick={() => setMobileMenuOpen(false)} className="block px-4 py-2.5 text-sm font-medium text-gray-600 hover:bg-cyan-50 rounded-xl">首页</Link>
            <Link to="/category" onClick={() => setMobileMenuOpen(false)} className="block px-4 py-2.5 text-sm font-medium text-gray-600 hover:bg-cyan-50 rounded-xl">分类浏览</Link>
            {displayCategories.map((cat) => (
              <Link key={cat.id} to={`/category/${cat.slug}`} onClick={() => setMobileMenuOpen(false)} className="block px-4 py-2.5 text-sm font-medium text-gray-600 hover:bg-cyan-50 rounded-xl">
                {cat.name}
              </Link>
            ))}
            <Link to="/publish" onClick={() => setMobileMenuOpen(false)} className="block px-4 py-2.5 text-sm font-semibold text-cyan-600 hover:bg-cyan-50 rounded-xl">
              <i className="ri-add-line mr-2" />发布信息
            </Link>
          </div>
        )}
      </nav>

      {/* 底部波浪装饰 */}
      <div className="relative -mb-px">
        <svg viewBox="0 0 1440 60" fill="none" className="w-full h-6" preserveAspectRatio="none">
          <path d="M0 30 Q 180 5, 360 30 T 720 20 T 1080 30 T 1440 15 L 1440 60 L 0 60 Z" fill="currentColor" className="text-cyan-50/60" />
        </svg>
      </div>
    </>
  );
}
